import { FC } from 'react';
import { getColorValue } from '@shinetools/sunshine';
import { Area, ComposedChart, Line, Tooltip, XAxis, YAxis } from 'recharts';

import { LineGraphData } from 'features/Dashboard/types';

import {
  activeDot,
  GRAPH_ANIMATION_DURATION,
  GRAPH_ANIMATION_EASING,
  xAxisDateFormatter,
  yAxisLabelFormatter,
} from '../../../common';
import useDateLabel from '../../hooks/useDateLabels';
import useIntervalContext from '../../hooks/useIntervalContext';
import CartesianGridNoDashHorizontal from '../CartesianGridNoDashHorizontal';
import Graph from '../Graph';
import GraphCursor from '../GraphCursor';
import GraphLegend from '../GraphLegend';
import LineGraphTooltip from '../LineGraphTooltip';
import CustomCurrentAreaDot from './components/CustomCurrentAreaDot';
import { FULL_WIDTH_LINE_GRAPH_HEIGHT } from './constants';
import { shouldShowCurrentDateDot } from './helpers/shouldShowCurrentDateDot';

interface FullWidthLineGraphProps {
  data: LineGraphData[];
  dateTickInterval?: number;
}

const FullWidthLineGraph: FC<FullWidthLineGraphProps> = ({
  data,
  dateTickInterval,
}) => {
  const { granularity, interval } = useIntervalContext();

  const currentPeriod = useDateLabel(
    interval.currentInterval.from,
    interval.type,
  );
  const oldPeriod = useDateLabel(
    interval.previousInterval?.from,
    interval.type,
  );

  return (
    <Graph height={FULL_WIDTH_LINE_GRAPH_HEIGHT}>
      <Graph.Header>
        <GraphLegend backgroundColor="grey.800" label={currentPeriod} />
        {oldPeriod ? (
          <GraphLegend backgroundColor="grey.500" label={oldPeriod} />
        ) : null}
      </Graph.Header>
      <Graph.Body>
        <ComposedChart data={data} margin={{ left: 0, top: 5 }}>
          <Tooltip
            content={<LineGraphTooltip />}
            cursor={<GraphCursor />}
            isAnimationActive={false}
            position={{ y: -50 }}
          />

          <CartesianGridNoDashHorizontal />

          <XAxis
            dataKey="newDate"
            dx={12}
            interval={dateTickInterval ?? 'preserveStartEnd'}
            stroke="white"
            tick={{ fill: getColorValue('grey.500'), fontSize: 10 }}
            tickFormatter={(label) => xAxisDateFormatter(label, granularity)}
            tickLine={false}
          />
          <YAxis
            dy={-6}
            orientation="right"
            padding={{ top: 10 }}
            stroke="white"
            strokeWidth={0.1}
            style={{ textAnchor: 'middle' }}
            tick={{ fill: getColorValue('grey.500'), fontSize: 10 }}
            tickFormatter={yAxisLabelFormatter}
            tickLine={false}
          />

          <Line
            activeDot={activeDot('grey.400')}
            animationDuration={GRAPH_ANIMATION_DURATION}
            animationEasing={GRAPH_ANIMATION_EASING}
            dataKey="oldValue"
            dot={false}
            stroke={getColorValue('grey.400')}
            strokeLinecap="round"
            strokeWidth={2}
            type="monotone"
          />

          <>
            <defs>
              <linearGradient id="colorUv" x1="0" x2="0" y1="0" y2="1">
                <stop
                  offset="5%"
                  stopColor="rgba(37, 36, 29, 0.10)"
                  stopOpacity={1}
                />
                <stop
                  offset="95%"
                  stopColor="rgba(37, 36, 29, 0.10)"
                  stopOpacity={0}
                />
              </linearGradient>
            </defs>
            <Area
              activeDot={activeDot('grey.800')}
              animationDuration={GRAPH_ANIMATION_DURATION}
              animationEasing={GRAPH_ANIMATION_EASING}
              dataKey="newValue"
              // @ts-expect-error Works fine, unsure what the typing issue is
              dot={(point) => {
                const shouldShowDot = shouldShowCurrentDateDot(
                  granularity,
                  point?.payload?.newDate,
                );

                if (shouldShowDot) {
                  return <CustomCurrentAreaDot {...point} />;
                }

                return null;
              }}
              fill="url(#colorUv)"
              fillOpacity={1}
              stroke={getColorValue('grey.800')}
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              type="monotone"
            />
          </>
        </ComposedChart>
      </Graph.Body>
    </Graph>
  );
};

export default FullWidthLineGraph;
